import { ErrorHandler, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { JsonHttpService } from '../http/json-http.service';
import { LoadingPanelService } from '../loading-panel/loading-panel.service';
import { GenericModalService, ModalSettingsConfirmMultiButton, ModalMultiButton, ModalSettingsMessage } from '../generic-modal/generic-modal.service';
import { GenericErrorHandlingDbErrorsService } from './generic-error-handling-db-errors.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ApplicationInsightsService } from '../insights/application-insights.service';

@Injectable({
  providedIn: 'root'
})
export class GenericErrorHandlingService {
  private http = inject(JsonHttpService);
  private router = inject(Router);
  private genericModalService = inject(GenericModalService);
  private loadingPanelService = inject(LoadingPanelService);
  private errorHandler = inject(GenericErrorHandler);
  private genericErrorHandlingDbErrorsService = inject(GenericErrorHandlingDbErrorsService);

  newErrorSubject = new Subject<LinkError>();
  public newErrorObserver = this.newErrorSubject.asObservable();

  genericErrors: unknown[] = [];
  httpErrors: unknown[] = [];
  allErrors: LinkError[] = [];
  modalCurrentlyShown = false;

  public subscribeToErrors() {
    this.http.errorHandler.subscribe(e => void this.handleHttpError(e));
    const genericErrorHandler = this.errorHandler;
    if (genericErrorHandler) {
      genericErrorHandler.errorHandler.subscribe((e) => this.handleGenericError(e));
    }
  }

  public getGenericErrors(): any[] {
    return this.genericErrors;
  }

  public getHttpErrors(): any[] {
    return this.httpErrors;
  }

  public getAllErrors(): LinkError[] {
    return this.allErrors;
  }

  handleGenericError(ex: any) {
    if (ex) {
      console.group('GENERIC ERROR');
      console.error(ex);
      console.groupEnd();

      this.genericErrors.push(ex);

      let logMessage = 'A generic exception has occurred in the frontend.';

      if (ex.stack) {
        logMessage = ex.stack;
      }
      else if (ex.message) {
        logMessage = ex.message;
      }
      else {
        logMessage += ex.toString();
      }

      this.handleError('FrontEndGenericError', logMessage /*, ex*/); // We can't send the whole ex object to the backend because the json serializer complains about circular references.
    }
  }

  async handleHttpError(ex: any) {
    if (ex) {
      ex = await this.checkBlob(ex);

      console.group('HTTP ERROR');
      console.error(ex);
      console.groupEnd();

      this.httpErrors.push(ex);

      const result = this.generateLogMessage(ex, 'An http exception has occurred in the frontend');

      if (ex.status !== null && ex.status !== undefined) {

        switch (ex.status) {
          case 401: // Ignore 401 errors because they just redirects to login page
            break;
          case 404:
            this.handleError('FrontEndHttpError', ex.statusText + '\n' + ex.message);
            break;
          case 0: // Status code 0 normally indicates missing connection to the backend
            // Don't handle this error anymore. The browser should handle this, and the next call will succeed.
            // We are experiencing this error a lot more in Link3 - maybe because of nginx or because of a newer version of Angular.
            // This should be investigated more - but for now we disable this error and just logs it to the console.
            console.error('HttpCode 0 detected!');
            console.error(ex);
            //this.handleNoInternetConnection();
            break;
          default:
            this.handleError('FrontEndHttpError', result.logMessage, result.additionalLog);
            break;
        }
      }
    }
  }

  private checkBlob(err: any) {
    if (err instanceof HttpErrorResponse && err.error instanceof Blob && err.error.type === 'application/json') {
      return new Promise<any>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e: Event) => {
          try {
            const errmsg = JSON.parse((e.target as any).result);
            resolve(new HttpErrorResponse({
              error: errmsg,
              headers: err.headers,
              status: err.status,
              statusText: err.statusText,
              url: err.url
            }));
          } catch (error) {
            reject(err);
          }
        };
        reader.onerror = () => {
          reject(err);
        };
        reader.readAsText(err.error);
      });
    }

    return Promise.resolve(err);
  }

  generateLogMessage(ex: any, initialLogMessage: string): any {
    const result: any = { logMessage: initialLogMessage };

    try {
      result.additionalLog = ex.error || ex.message || ex.json();
      const logMessages: string[] = [];
      this.traverseExceptions(result.additionalLog, logMessages);
      result.logMessage = logMessages.join('\r\n');
    }
    catch {
      result.logMessage = ex.text();
    }

    result.logMessage = this.genericErrorHandlingDbErrorsService.checkForDbErrors(result.logMessage);
    return result;
  }

  traverseExceptions(ex: any, logMessages: string[]) {
    if (typeof ex === 'string' || ex instanceof String) {
      logMessages.push(ex + ''); // If the given 'exception' is not an object, but just a string - add that string to the error-logs
    }
    else if (ex.ExceptionMessage) {
      let logMessage = `Exception message: ${ex.ExceptionMessage}\r\n`;
      logMessage += `Exception type: ${ex.ExceptionType}\r\n`;
      logMessage += `Message: ${ex.Message}\r\n`;
      logMessage += `StackTrace: ${ex.StackTrace}`;

      if (ex.InnerException) {
        logMessage += '\r\n';
        logMessage += '\r\n';
        logMessage += '-------------------------- Inner Exception --------------------------';
        logMessage += '\r\n';
        logMessages.push(logMessage);
        this.traverseExceptions(ex.InnerException, logMessages);
      }
      else {
        logMessages.push(logMessage);
      }
    }
    else {
      logMessages.push(JSON.stringify(ex)); // TODO: Gør dette bedre - jeg tror det er når der smide AggregateExceptions fra async metoder på backenden, at vi ser den her.
    }
  }

  handleError(category: string, errorMessage: string, additionalInfo?: any, customHeader?: string) {
    this.loadingPanelService.hideLoadingPanel(); // Alway hide the loading panel, so it won't disable the menu...

    //this.loggingService.logError(category, errorMessage, additionalInfo).catch((ex) => { console.group('An error occurred while calling backend-log'); console.error(ex); console.groupEnd(); });

    const newError = new LinkError(category, errorMessage, new Date(), additionalInfo);
    if (!this.modalCurrentlyShown) {
      this.modalCurrentlyShown = true;

      customHeader = customHeader || `An error has occurred (${category})`;

      this.genericModalService.confirmMultiButton(this.generateDialogOptions(customHeader, errorMessage)).then(result => {
        if (result === 'showlog') {
          this.modalCurrentlyShown = false;
          void this.router.navigate(['generic-errors']);
        }
        else {
          this.modalCurrentlyShown = false;
        }
      }).catch(() => { this.modalCurrentlyShown = false; });
    }

    this.allErrors.push(newError);
    this.newErrorSubject.next(newError);
  }

  handleNoInternetConnection() {
    this.loadingPanelService.hideLoadingPanel(); // Alway hide the loading panel, so it won't disable the menu...

    if (!this.modalCurrentlyShown) {
      this.modalCurrentlyShown = true;
      const settings = new ModalSettingsMessage();
      settings.size = 'lg';
      settings.backdrop = 'static';
      settings.title = 'Missing internet';
      settings.body = `Error details: <pre id='errorElement'>No connection to the backend. Please check your internet connection</pre>`;

      const refresh = () => {
        this.modalCurrentlyShown = false;
        window.location.reload();
      };

      this.genericModalService.message(settings).then(() => refresh()).catch(() => refresh());
    }
  }

  generateDialogOptions(header: string, logMessage: string) {
    const settings = new ModalSettingsConfirmMultiButton();
    settings.size = 'lg';
    settings.backdrop = 'static';
    settings.title = header;
    settings.body = `Error details: <pre id='errorElement'>${logMessage}</pre>`;

    settings.buttons = [];
    //settings.buttons.push(new ModalMultiButton('Send error report', 'sendBugReport', 'btn btn-danger'));
    settings.buttons.push(new ModalMultiButton('Show error log', 'showlog', 'btn btn-link'));
    settings.buttons.push(new ModalMultiButton('Close', null, 'btn btn-link'));

    return settings;
  }
}

@Injectable({
  providedIn: 'root'
})
export class GenericErrorHandler implements ErrorHandler {
  private appInsightsService = inject(ApplicationInsightsService);

  private errorHandlerSubject = new BehaviorSubject<unknown>(null);
  public errorHandler = this.errorHandlerSubject.asObservable();

  handleError(error: unknown) {
    if (error instanceof Error) {
      this.appInsightsService.logException(error);
    }
    this.errorHandlerSubject.next(error);
  }
}

class LinkError {
  constructor(
    public category: string,
    public errorMessage: string,
    public createdDate: Date,
    public additionalInfo: unknown
  ) {
  }
}
